import { useState, useEffect, useRef } from "react";
import { motion, useDragControls, useMotionValue } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { Checkbox } from "../../components/ui/checkbox";
import useCompanies from "../../api/company";
import { smartCompressImage } from "../../lib/imageCompression";
import {
  Plus,
  Building2,
  Trash,
  Pencil,
  Search,
  Mail,
  Phone,
  Globe,
  Shield,
  Check,
  AlertCircle,
  X,
  ImageIcon,
  Eye,
  EyeOff,
  Copy,
  Hash,
} from "lucide-react";

const AVAILABLE_FEATURES = [
  { id: "inbox", label: "Inbox" },
  { id: "items", label: "Items" },
  { id: "shop", label: "Winkel" },
  { id: "orders", label: "Bestellingen" },
  { id: "reservations", label: "Reservaties" },
  { id: "zaalplanner", label: "Zaalplanner-app (zaalplanner.zichtbaarstudio.be)" },
  { id: "events", label: "Inschrijvingen" },
  { id: "jobs", label: "Sollicitaties" },
  { id: "popups", label: "Pop-ups" },
  { id: "configurator", label: "Configurator" },
  { id: "files", label: "Bestanden" },
  { id: "tickets", label: "Tickets" },
  { id: "statistics", label: "Statistieken" },
  { id: "learningPlatform", label: "Leerplatform" },
  { id: "aiVideoAnalyst", label: "AI Video Analist" },
];

const CompaniesManager = () => {
  const { company: currentCompany } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { getAllCompanies, addCompany, updateCompany, deleteCompany, uploadLogo, deleteLogo, getLogoUrl } =
    useCompanies();

  const [companies, setCompanies] = useState([]);
  const [logoUrls, setLogoUrls] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [revealedIds, setRevealedIds] = useState({});
  const [copiedId, setCopiedId] = useState(null);

  const toggleRevealId = (id) => {
    setRevealedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleCopyId = async (id) => {
    try {
      await navigator.clipboard.writeText(String(id));
      setCopiedId(id);
      setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 1500);
    } catch (err) {
      console.error("Error copying company id:", err);
      setError("Kon de ID niet naar het klembord kopiëren.");
    }
  };

  // Form states
  const [newCompany, setNewCompany] = useState({
    name: "",
    email: "",
    password: "",
    phone_number: "",
    address: "",
    btw_number: "",
    domeinnaam: "",
    isAdmin: 0,
    permissions: {},
  });
  const [editingCompany, setEditingCompany] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [editLogoUrl, setEditLogoUrl] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [pendingLogoData, setPendingLogoData] = useState(null);
  const editLogoInputRef = useRef(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 640 : false,
  );
  const sheetRef = useRef(null);
  const sheetContentRef = useRef(null);
  const deleteSheetRef = useRef(null);
  const sheetDragControls = useDragControls();
  const deleteDragControls = useDragControls();
  const sheetY = useMotionValue(0);
  const deleteSheetY = useMotionValue(0);
  const isSheetScrolledToTop = useRef(true);
  const sheetTouchStartY = useRef(null);
  const sheetDragging = useRef(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Attach native touch listeners for pull-down-to-dismiss from anywhere in the content
  useEffect(() => {
    isSheetScrolledToTop.current = true;
    sheetTouchStartY.current = null;
    sheetDragging.current = false;

    const el = sheetContentRef.current;
    if (!el || !isMobile) return;

    const onScroll = () => {
      isSheetScrolledToTop.current = el.scrollTop <= 0;
    };
    const onTouchStart = (e) => {
      sheetTouchStartY.current = e.touches[0].clientY;
      sheetDragging.current = false;
    };
    const onTouchMove = (e) => {
      if (sheetDragging.current) {
        e.preventDefault();
        return;
      }
      if (sheetTouchStartY.current === null) return;
      const deltaY = e.touches[0].clientY - sheetTouchStartY.current;
      if (deltaY > 8 && isSheetScrolledToTop.current) {
        sheetDragging.current = true;
        e.preventDefault();
        const t = e.touches[0];
        sheetDragControls.start(
          new PointerEvent("pointerdown", {
            clientX: t.clientX,
            clientY: t.clientY,
            pointerId: 1,
            pointerType: "touch",
            bubbles: true,
          }),
        );
      }
    };
    const onTouchEnd = () => {
      sheetTouchStartY.current = null;
      sheetDragging.current = false;
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [isMobile, sheetDragControls, dialogOpen]);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const data = await getAllCompanies();
      setCompanies(data || []);
      // Fetch logo URLs for companies that have a logo
      const urls = {};
      await Promise.all(
        (data || [])
          .filter((c) => c.logo)
          .map(async (c) => {
            const result = await getLogoUrl(c.id);
            if (result?.logoUrl) urls[c.id] = result.logoUrl;
          })
      );
      setLogoUrls(urls);
    } catch (err) {
      console.error("Error fetching companies:", err);
      setError("Er is een fout opgetreden bij het ophalen van de bedrijven.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const inputValue = type === "checkbox" ? (checked ? 1 : 0) : value;

    if (editingCompany) {
      setEditingCompany({
        ...editingCompany,
        [name]: inputValue,
      });
    } else {
      setNewCompany({
        ...newCompany,
        [name]: inputValue,
      });
    }
  };
  const handlePermissionChange = (featureId, checked) => {
    if (editingCompany) {
      setEditingCompany({
        ...editingCompany,
        permissions: {
          ...editingCompany.permissions,
          [featureId]: checked,
        },
      });
    } else {
      setNewCompany({
        ...newCompany,
        permissions: {
          ...newCompany.permissions,
          [featureId]: checked,
        },
      });
    }
  };
  const handleCreateCompany = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      const createdCompany = await addCompany(newCompany);

      // Upload pending logo if any
      if (pendingLogoData && createdCompany?.id) {
        try {
          const logoResult = await uploadLogo(pendingLogoData, createdCompany.id);
          if (logoResult?.logoUrl) {
            logoUrls[createdCompany.id] = logoResult.logoUrl;
            setLogoUrls({ ...logoUrls });
          }
        } catch (logoErr) {
          console.error("Error uploading logo for new company:", logoErr);
        }
      }

      setCompanies([...companies, createdCompany]);
      setSuccess("Bedrijf is succesvol aangemaakt!");
      setNewCompany({
        name: "",
        email: "",
        password: "",
        phone_number: "",
        address: "",
        btw_number: "",
        domeinnaam: "",
        isAdmin: 0,
      });
      setPendingLogoData(null);
      setEditLogoUrl(null);
      setDialogOpen(false);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error creating company:", err);
      setError("Er is een fout opgetreden bij het aanmaken van het bedrijf.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      const updatedCompany = await updateCompany(
        editingCompany.id,
        editingCompany,
      );
      setCompanies(
        companies.map((comp) =>
          comp.id === updatedCompany.id ? updatedCompany : comp,
        ),
      );
      setSuccess("Bedrijf is succesvol bijgewerkt!");
      setEditingCompany(null);
      setDialogOpen(false);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error updating company:", err);
      setError("Er is een fout opgetreden bij het bijwerken van het bedrijf.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCompany = async () => {
    if (!companyToDelete) return;

    setIsSubmitting(true);
    setError("");

    try {
      await deleteCompany(companyToDelete.id);
      setCompanies(companies.filter((comp) => comp.id !== companyToDelete.id));
      setSuccess("Bedrijf is succesvol verwijderd!");
      setCompanyToDelete(null);
      setDeleteDialogOpen(false);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error deleting company:", err);
      setError(
        "Er is een fout opgetreden bij het verwijderen van het bedrijf.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditDialog = (company) => {
    setEditingCompany(company);
    setEditLogoUrl(null);
    setPendingLogoData(null);
    setDialogOpen(true);
    // Fetch logo URL for this company
    if (company.logo) {
      getLogoUrl(company.id).then((data) => {
        if (data?.logoUrl) setEditLogoUrl(data.logoUrl);
      });
    }
  };

  const handleEditLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isHeic = /\.(heic|heif)$/i.test(file.name || "");
    if (!file.type.startsWith("image/") && !isHeic) {
      setError("Alleen afbeeldingen zijn toegestaan.");
      return;
    }
    setLogoUploading(true);
    setError("");
    try {
      // Comprimeer (incl. HEIC->JPEG) zodat het logo ruim onder de
      // serverlimiet blijft, i.p.v. grote bestanden hard te weigeren.
      const compressedDataUrl = await smartCompressImage(file);
      if (editingCompany) {
        // Editing: upload immediately
        const result = await uploadLogo(compressedDataUrl, editingCompany.id);
        if (result?.logoUrl) {
          setEditLogoUrl(result.logoUrl);
          setEditingCompany((prev) => ({ ...prev, logo: result.logoKey }));
        }
      } else {
        // Creating: store for later
        setPendingLogoData(compressedDataUrl);
        setEditLogoUrl(compressedDataUrl); // Preview using compressed data URL
      }
    } catch (err) {
      setError("Fout bij het uploaden van het logo.");
    } finally {
      setLogoUploading(false);
    }
  };

  const handleEditLogoDelete = async () => {
    if (editingCompany) {
      setLogoUploading(true);
      try {
        await deleteLogo(editingCompany.id);
        setEditLogoUrl(null);
        setEditingCompany((prev) => ({ ...prev, logo: null }));
      } catch (err) {
        setError("Fout bij het verwijderen van het logo.");
      } finally {
        setLogoUploading(false);
      }
    } else {
      // Creating: just clear pending
      setPendingLogoData(null);
      setEditLogoUrl(null);
    }
  };

  const openDeleteDialog = (company) => {
    setCompanyToDelete(company);
    setDeleteDialogOpen(true);
  };

  const filteredCompanies = companies.filter(
    (company) =>
      company.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      company.email?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div
          className={`w-8 h-8 rounded-full border-2 animate-spin ${isDark ? "border-white/[0.08] border-t-white/40" : "border-gray-200 border-t-gray-600"}`}
        />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[
          { label: "Totaal", value: companies.length, icon: Building2 },
          {
            label: "Admins",
            value: companies.filter((c) => c.isAdmin === 1).length,
            icon: Shield,
            dot: isDark ? "bg-amber-400" : "bg-amber-500",
          },
          {
            label: "Gebruikers",
            value: companies.filter((c) => c.isAdmin !== 1).length,
            icon: Building2,
            dot: isDark ? "bg-emerald-400" : "bg-emerald-500",
          },
        ].map((s) => (
          <div
            key={s.label}
            className={`rounded-xl p-4 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
          >
            <div className="flex items-center gap-1.5 mb-2">
              <s.icon
                className={`h-3.5 w-3.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
              />
              <span
                className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                {s.label}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {s.dot && <div className={`h-2 w-2 rounded-full ${s.dot}`} />}
              <span
                className={`text-xl font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
              >
                {s.value}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <h1
          className={`text-[15px] font-semibold tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}
        >
          Bedrijven
        </h1>
        <button
          onClick={() => {
            setEditingCompany(null);
            setNewCompany({
              name: "",
              email: "",
              password: "",
              phone_number: "",
              address: "",
              btw_number: "",
              domeinnaam: "",
              isAdmin: 0,
              permissions: {},
            });
            setDialogOpen(true);
          }}
          className={`h-9 px-4 rounded-lg text-[13px] font-medium inline-flex items-center gap-2 transition-colors ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
        >
          <Plus className="h-3.5 w-3.5" />
          Nieuw bedrijf
        </button>
      </div>

      {/* Search */}
      <div
        className={`rounded-xl p-3 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
      >
        <div className="relative">
          <Search
            className={`absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
          />
          <input
            placeholder="Zoek op bedrijfsnaam of e-mail..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`w-full h-9 pl-9 pr-3 rounded-lg text-[13px] outline-none transition-colors ${isDark ? "bg-white/[0.04] border border-white/[0.06] text-white placeholder-gray-600 focus:border-white/[0.12]" : "bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
          />
        </div>
      </div>

      {/* Create/Edit Modal */}
      {dialogOpen && (
        <div
          className={`fixed inset-0 z-50 flex ${isMobile ? "items-end" : "items-center"} justify-center`}
        >
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setDialogOpen(false)}
          />
          <motion.div
            ref={sheetRef}
            initial={isMobile ? { y: "100%" } : { scale: 0.95, opacity: 0 }}
            animate={isMobile ? { y: 0 } : { scale: 1, opacity: 1 }}
            exit={isMobile ? { y: "100%" } : { scale: 0.95, opacity: 0 }}
            transition={
              isMobile
                ? { type: "spring", damping: 35, stiffness: 400 }
                : { type: "spring", duration: 0.3 }
            }
            drag={isMobile ? "y" : false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.6 }}
            dragListener={false}
            dragControls={sheetDragControls}
            onDragEnd={(_, info) => {
              if (info.offset.y > 80 || info.velocity.y > 500) {
                setDialogOpen(false);
              }
            }}
            style={
              isMobile
                ? {
                  y: sheetY,
                  borderTopLeftRadius: "1rem",
                  borderTopRightRadius: "1rem",
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                }
                : undefined
            }
            className={`relative w-full ${isMobile ? "max-w-full max-h-[85dvh] flex flex-col" : "rounded-xl max-w-2xl mx-4 max-h-[80vh] flex flex-col"} ${isDark ? "bg-[#141414] border border-white/[0.06]" : "bg-white border border-gray-200"}`}
          >
            {isMobile && (
              <div
                onPointerDown={(e) => sheetDragControls.start(e)}
                className="flex justify-center pt-3 pb-1 shrink-0 cursor-grab active:cursor-grabbing touch-none"
                style={{ touchAction: "none" }}
              >
                <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
              </div>
            )}
            <div
              className="p-6 overflow-y-auto overscroll-contain flex-1"
              ref={isMobile ? sheetContentRef : undefined}
            >
              <div className="flex items-center justify-between mb-1">
                <h2
                  className={`text-[14px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  {editingCompany
                    ? "Bedrijf bewerken"
                    : "Nieuw bedrijf aanmaken"}
                </h2>
                <button
                  onClick={() => setDialogOpen(false)}
                  className={`h-7 w-7 rounded-lg flex items-center justify-center ${isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <p
                className={`text-[12px] mb-5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                {editingCompany
                  ? "Bewerk de gegevens van dit bedrijf."
                  : "Vul de gegevens in om een nieuw bedrijf aan te maken."}
              </p>

              <form
                onSubmit={
                  editingCompany ? handleUpdateCompany : handleCreateCompany
                }
                className="space-y-4"
              >
                {/* Logo upload */}
                <div>
                  <label
                    className={`text-[11px] font-medium uppercase tracking-wider block mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    Logo
                  </label>
                  <input
                    ref={editLogoInputRef}
                    type="file"
                    accept="image/*,.heic,.heif"
                    className="hidden"
                    onChange={handleEditLogoUpload}
                  />
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => editLogoInputRef.current?.click()}
                      disabled={logoUploading}
                      className={`relative h-10 w-10 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center transition-colors ${editLogoUrl
                        ? `p-1 ${isDark ? "bg-white/[0.06] border border-white/[0.08]" : "bg-gray-50 border border-gray-200"}`
                        : `border border-dashed ${isDark ? "border-white/[0.08] hover:border-white/20" : "border-gray-200 hover:border-gray-300"}`
                        }`}
                    >
                      {logoUploading ? (
                        <div
                          className={`h-3.5 w-3.5 rounded-full border-[1.5px] animate-spin ${isDark ? "border-white/10 border-t-white/50" : "border-gray-200 border-t-gray-500"}`}
                        />
                      ) : editLogoUrl ? (
                        <img
                          src={editLogoUrl}
                          alt="Logo"
                          className="h-full w-full object-contain"
                        />
                      ) : (
                        <ImageIcon className={`h-4 w-4 ${isDark ? "text-gray-600" : "text-gray-400"}`} />
                      )}
                    </button>
                    <span
                      className={`text-[12px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                    >
                      {editLogoUrl ? (
                        <button
                          type="button"
                          onClick={handleEditLogoDelete}
                          disabled={logoUploading}
                          className={`text-[12px] transition-colors ${isDark ? "text-gray-500 hover:text-red-400" : "text-gray-400 hover:text-red-500"}`}
                        >
                          Verwijder
                        </button>
                      ) : (
                        "PNG, JPG, HEIC \u2014 wordt automatisch verkleind"
                      )}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      className={`text-[11px] font-medium uppercase tracking-wider block mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                    >
                      Bedrijfsnaam *
                    </label>
                    <input
                      name="name"
                      value={
                        editingCompany ? editingCompany.name : newCompany.name
                      }
                      onChange={handleInputChange}
                      placeholder="Bedrijfsnaam"
                      required
                      className={`w-full h-9 px-3 rounded-lg text-[13px] outline-none transition-colors ${isDark ? "bg-white/[0.04] border border-white/[0.06] text-white placeholder-gray-600 focus:border-white/[0.12]" : "bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                    />
                  </div>

                  <div>
                    <label
                      className={`text-[11px] font-medium uppercase tracking-wider block mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                    >
                      E-mail *
                    </label>
                    <input
                      name="email"
                      type="email"
                      value={
                        editingCompany ? editingCompany.email : newCompany.email
                      }
                      onChange={handleInputChange}
                      placeholder="email@bedrijf.be"
                      required
                      className={`w-full h-9 px-3 rounded-lg text-[13px] outline-none transition-colors ${isDark ? "bg-white/[0.04] border border-white/[0.06] text-white placeholder-gray-600 focus:border-white/[0.12]" : "bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                    />
                  </div>

                  {!editingCompany && (
                    <div>
                      <label
                        className={`text-[11px] font-medium uppercase tracking-wider block mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                      >
                        Wachtwoord *
                      </label>
                      <input
                        name="password"
                        type="password"
                        value={newCompany.password}
                        onChange={handleInputChange}
                        placeholder="Minimaal 8 tekens"
                        required={!editingCompany}
                        minLength={8}
                        className={`w-full h-9 px-3 rounded-lg text-[13px] outline-none transition-colors ${isDark ? "bg-white/[0.04] border border-white/[0.06] text-white placeholder-gray-600 focus:border-white/[0.12]" : "bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                      />
                    </div>
                  )}

                  <div>
                    <label
                      className={`text-[11px] font-medium uppercase tracking-wider block mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                    >
                      Telefoonnummer
                    </label>
                    <input
                      name="phone_number"
                      value={
                        editingCompany
                          ? editingCompany.phone_number
                          : newCompany.phone_number
                      }
                      onChange={handleInputChange}
                      placeholder="+32 4 12 34 56 78"
                      className={`w-full h-9 px-3 rounded-lg text-[13px] outline-none transition-colors ${isDark ? "bg-white/[0.04] border border-white/[0.06] text-white placeholder-gray-600 focus:border-white/[0.12]" : "bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                    />
                  </div>

                  <div>
                    <label
                      className={`text-[11px] font-medium uppercase tracking-wider block mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                    >
                      Adres
                    </label>
                    <input
                      name="address"
                      value={
                        editingCompany
                          ? editingCompany.address
                          : newCompany.address
                      }
                      onChange={handleInputChange}
                      placeholder="Straat 123, Stad"
                      className={`w-full h-9 px-3 rounded-lg text-[13px] outline-none transition-colors ${isDark ? "bg-white/[0.04] border border-white/[0.06] text-white placeholder-gray-600 focus:border-white/[0.12]" : "bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                    />
                  </div>

                  <div>
                    <label
                      className={`text-[11px] font-medium uppercase tracking-wider block mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                    >
                      BTW Nummer
                    </label>
                    <input
                      name="btw_number"
                      value={
                        editingCompany
                          ? editingCompany.btw_number
                          : newCompany.btw_number
                      }
                      onChange={handleInputChange}
                      placeholder="BE0123456789"
                      className={`w-full h-9 px-3 rounded-lg text-[13px] outline-none transition-colors ${isDark ? "bg-white/[0.04] border border-white/[0.06] text-white placeholder-gray-600 focus:border-white/[0.12]" : "bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                    />
                  </div>

                  <div>
                    <label
                      className={`text-[11px] font-medium uppercase tracking-wider block mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                    >
                      Domeinnaam
                    </label>
                    <input
                      name="domeinnaam"
                      value={
                        editingCompany
                          ? editingCompany.domeinnaam
                          : newCompany.domeinnaam
                      }
                      onChange={handleInputChange}
                      placeholder="www.bedrijf.be"
                      className={`w-full h-9 px-3 rounded-lg text-[13px] outline-none transition-colors ${isDark ? "bg-white/[0.04] border border-white/[0.06] text-white placeholder-gray-600 focus:border-white/[0.12]" : "bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label
                      className={`text-[11px] font-medium uppercase tracking-wider block mb-2 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                    >
                      Permissies
                    </label>
                    <div
                      className={`grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 rounded-lg ${isDark ? "bg-white/[0.02] border border-white/[0.06]" : "bg-gray-50 border border-gray-200"}`}
                    >
                      {AVAILABLE_FEATURES.map((feature) => (
                        <label
                          key={feature.id}
                          className={`flex items-center gap-2 text-[13px] cursor-pointer px-2 py-1.5 rounded-md transition-colors ${isDark ? "hover:bg-white/[0.04] text-gray-300" : "hover:bg-white text-gray-700"}`}
                        >
                          <Checkbox
                            checked={
                              editingCompany
                                ? !!editingCompany.permissions?.[feature.id]
                                : !!newCompany.permissions?.[feature.id]
                            }
                            onCheckedChange={(checked) =>
                              handlePermissionChange(feature.id, !!checked)
                            }
                          />
                          {feature.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label
                      className={`flex items-center gap-2 text-[13px] cursor-pointer ${isDark ? "text-gray-300" : "text-gray-700"}`}
                    >
                      <Checkbox
                        checked={
                          editingCompany
                            ? editingCompany.isAdmin === 1
                            : newCompany.isAdmin === 1
                        }
                        onCheckedChange={(checked) => {
                          const value = checked ? 1 : 0;
                          if (editingCompany) {
                            setEditingCompany({
                              ...editingCompany,
                              isAdmin: value,
                            });
                          } else {
                            setNewCompany({ ...newCompany, isAdmin: value });
                          }
                        }}
                      />
                      <Shield className="h-3.5 w-3.5" />
                      Administrator rechten
                    </label>
                  </div>
                </div>

              </form>
            </div>

            {/* Sticky footer buttons */}
            <div
              className={`px-6 py-4 border-t shrink-0 flex justify-end gap-2 ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
            >
              <button
                type="button"
                onClick={() => setDialogOpen(false)}
                className={`h-9 px-4 rounded-lg text-[13px] font-medium border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
              >
                Annuleren
              </button>
              <button
                onClick={editingCompany ? handleUpdateCompany : handleCreateCompany}
                disabled={isSubmitting}
                className={`h-9 px-4 rounded-lg text-[13px] font-medium inline-flex items-center gap-2 transition-colors disabled:opacity-40 ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
              >
                {isSubmitting ? (
                  <>
                    <div
                      className={`h-3.5 w-3.5 rounded-full border-2 animate-spin ${isDark ? "border-black/20 border-t-black" : "border-white/20 border-t-white"}`}
                    />
                    {editingCompany ? "Bijwerken..." : "Aanmaken..."}
                  </>
                ) : (
                  <>{editingCompany ? "Bijwerken" : "Aanmaken"}</>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteDialogOpen && (
        <div
          className={`fixed inset-0 z-50 flex ${isMobile ? "items-end" : "items-center"} justify-center`}
        >
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setDeleteDialogOpen(false)}
          />
          <motion.div
            ref={deleteSheetRef}
            initial={isMobile ? { y: "100%" } : { scale: 0.95, opacity: 0 }}
            animate={isMobile ? { y: 0 } : { scale: 1, opacity: 1 }}
            exit={isMobile ? { y: "100%" } : { scale: 0.95, opacity: 0 }}
            transition={
              isMobile
                ? { type: "spring", damping: 35, stiffness: 400 }
                : { type: "spring", duration: 0.3 }
            }
            drag={isMobile ? "y" : false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.6 }}
            dragListener={false}
            dragControls={deleteDragControls}
            onDragEnd={(_, info) => {
              if (info.offset.y > 80 || info.velocity.y > 500) {
                setDeleteDialogOpen(false);
              }
            }}
            style={
              isMobile
                ? {
                  y: deleteSheetY,
                  borderTopLeftRadius: "1rem",
                  borderTopRightRadius: "1rem",
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                }
                : undefined
            }
            className={`relative w-full ${isMobile ? "max-w-full" : "rounded-xl max-w-md mx-4"} ${isDark ? "bg-[#141414] border border-white/[0.06]" : "bg-white border border-gray-200"}`}
          >
            {isMobile && (
              <div
                onPointerDown={(e) => deleteDragControls.start(e)}
                className="flex justify-center pt-3 pb-1 shrink-0 cursor-grab active:cursor-grabbing touch-none"
                style={{ touchAction: "none" }}
              >
                <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
              </div>
            )}
            <div className="p-6">
              <h2
                className={`text-[14px] font-semibold mb-2 ${isDark ? "text-red-400" : "text-red-600"}`}
              >
                Bedrijf verwijderen
              </h2>
              <p
                className={`text-[13px] mb-5 ${isDark ? "text-gray-400" : "text-gray-600"}`}
              >
                Weet u zeker dat u het bedrijf{" "}
                <span
                  className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  {companyToDelete?.name}
                </span>{" "}
                wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setDeleteDialogOpen(false)}
                  className={`h-9 px-4 rounded-lg text-[13px] font-medium border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                >
                  Annuleren
                </button>
                <button
                  onClick={handleDeleteCompany}
                  disabled={isSubmitting}
                  className="h-9 px-4 rounded-lg text-[13px] font-medium inline-flex items-center gap-2 transition-colors disabled:opacity-40 bg-red-600 text-white hover:bg-red-700"
                >
                  {isSubmitting ? (
                    <>
                      <div className="h-3.5 w-3.5 rounded-full border-2 border-white/20 border-t-white animate-spin" />
                      Verwijderen...
                    </>
                  ) : (
                    <>
                      <Trash className="h-3.5 w-3.5" />
                      Verwijderen
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Notifications */}
      {error && (
        <div
          className={`flex items-center gap-2 px-4 py-3 rounded-xl text-[13px] ${isDark ? "bg-red-500/10 border border-red-500/20 text-red-400" : "bg-red-50 border border-red-200 text-red-700"}`}
        >
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div
          className={`flex items-center gap-2 px-4 py-3 rounded-xl text-[13px] ${isDark ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-400" : "bg-emerald-50 border border-emerald-200 text-emerald-700"}`}
        >
          <Check className="h-3.5 w-3.5 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* Companies list */}
      {filteredCompanies.length === 0 ? (
        <div
          className={`rounded-xl p-12 text-center ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
        >
          <div
            className={`w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
          >
            <Building2
              className={`h-6 w-6 ${isDark ? "text-gray-600" : "text-gray-400"}`}
            />
          </div>
          <h3
            className={`text-[14px] font-semibold mb-1 ${isDark ? "text-gray-300" : "text-gray-900"}`}
          >
            {searchQuery ? "Geen resultaten" : "Nog geen bedrijven"}
          </h3>
          <p
            className={`text-[13px] mb-5 max-w-md mx-auto ${isDark ? "text-gray-600" : "text-gray-500"}`}
          >
            {searchQuery
              ? "Geen bedrijven gevonden die overeenkomen met uw zoekopdracht."
              : "Er zijn nog geen bedrijven aangemaakt in het systeem."}
          </p>
          {searchQuery ? (
            <button
              onClick={() => setSearchQuery("")}
              className={`h-8 px-3 rounded-lg text-[12px] font-medium border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
            >
              Zoekopdracht wissen
            </button>
          ) : (
            <button
              onClick={() => setDialogOpen(true)}
              className={`h-9 px-4 rounded-lg text-[13px] font-medium inline-flex items-center gap-2 transition-colors ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
            >
              <Plus className="h-3.5 w-3.5" />
              Eerste bedrijf aanmaken
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredCompanies.map((company, i) => (
            <motion.div
              key={company.id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              className={`rounded-xl overflow-hidden ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
            >
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2.5 mb-1">
                      {logoUrls[company.id] ? (
                        <div
                          className={`flex-shrink-0 h-8 w-8 rounded-lg overflow-hidden p-1 ${isDark ? "bg-white/[0.06] border border-white/[0.08]" : "bg-gray-50 border border-gray-200"}`}
                        >
                          <img
                            src={logoUrls[company.id]}
                            alt={company.name}
                            className="h-full w-full object-contain"
                          />
                        </div>
                      ) : (
                        <div
                          className={`flex-shrink-0 h-8 w-8 rounded-lg flex items-center justify-center text-[12px] font-semibold ${isDark ? "bg-white/[0.08] text-gray-300" : "bg-gray-100 text-gray-600"}`}
                        >
                          {company.name?.charAt(0)?.toUpperCase() || "Z"}
                        </div>
                      )}
                      <h3
                        className={`text-[14px] font-semibold truncate ${isDark ? "text-white" : "text-gray-900"}`}
                      >
                        {company.name}
                      </h3>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${company.isAdmin === 1
                          ? isDark
                            ? "bg-amber-500/15 text-amber-400"
                            : "bg-amber-50 text-amber-700"
                          : isDark
                            ? "bg-white/[0.06] text-gray-400"
                            : "bg-gray-100 text-gray-600"
                          }`}
                      >
                        {company.isAdmin === 1 && (
                          <Shield className="h-3 w-3" />
                        )}
                        {company.isAdmin === 1 ? "Admin" : "Gebruiker"}
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5">
                      <span
                        className={`inline-flex items-center gap-1 text-[12px] ${isDark ? "text-gray-400" : "text-gray-600"}`}
                      >
                        <Mail className="h-3 w-3" />
                        {company.email}
                      </span>
                      {company.phone_number && (
                        <span
                          className={`inline-flex items-center gap-1 text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                        >
                          <Phone className="h-3 w-3" />
                          {company.phone_number}
                        </span>
                      )}
                      {company.domeinnaam && (
                        <span
                          className={`inline-flex items-center gap-1 text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                        >
                          <Globe className="h-3 w-3" />
                          {company.domeinnaam}
                        </span>
                      )}
                    </div>

                    {/* Company ID — hidden by default, toggle to reveal, copy with one click */}
                    <div className="flex items-center gap-1.5 mt-2">
                      <span
                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-mono select-all ${isDark ? "bg-white/[0.04] text-gray-400 border border-white/[0.06]" : "bg-gray-50 text-gray-600 border border-gray-200"}`}
                      >
                        <Hash className="h-3 w-3 opacity-60" />
                        {revealedIds[company.id] ? company.id : "••••••••"}
                      </span>
                      <button
                        type="button"
                        onClick={() => toggleRevealId(company.id)}
                        title={revealedIds[company.id] ? "Verberg ID" : "Toon ID"}
                        className={`h-6 w-6 rounded-md flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"}`}
                      >
                        {revealedIds[company.id] ? (
                          <EyeOff className="h-3.5 w-3.5" />
                        ) : (
                          <Eye className="h-3.5 w-3.5" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCopyId(company.id)}
                        title="Kopieer ID"
                        className={`h-6 w-6 rounded-md flex items-center justify-center transition-colors ${copiedId === company.id
                          ? isDark
                            ? "text-emerald-400"
                            : "text-emerald-600"
                          : isDark
                            ? "text-gray-500 hover:text-white hover:bg-white/[0.06]"
                            : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                          }`}
                      >
                        {copiedId === company.id ? (
                          <Check className="h-3.5 w-3.5" />
                        ) : (
                          <Copy className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 ml-3">
                    <button
                      onClick={() => openEditDialog(company)}
                      className={`h-7 w-7 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => openDeleteDialog(company)}
                      disabled={company.id === currentCompany?.id}
                      className={`h-7 w-7 rounded-lg flex items-center justify-center transition-colors disabled:opacity-30 ${isDark ? "text-gray-500 hover:text-red-400 hover:bg-red-500/10" : "text-gray-400 hover:text-red-600 hover:bg-red-50"}`}
                    >
                      <Trash className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}

          <div
            className={`flex justify-between items-center pt-2 text-[12px] px-1 ${isDark ? "text-gray-600" : "text-gray-500"}`}
          >
            <span>
              {filteredCompanies.length}{" "}
              {filteredCompanies.length === 1 ? "bedrijf" : "bedrijven"}
            </span>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className={`text-[12px] transition-colors ${isDark ? "text-gray-500 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
              >
                Wis filter
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompaniesManager;
